// src/components/CV/CVPreview.jsx
import React from 'react';
import { escapeHtml } from '../../utils/helpers';

const CVPreview = ({ cvData, template, color, font, sectionOrder }) => {
    const { personal, summary, skills, certifications, languages, education, experience } = cvData;

    // Parse skills and languages into arrays
    const skillsArray = skills ? skills.split(',').map(s => s.trim()).filter(Boolean) : [];
    const languagesArray = languages ? languages.split(',').map(s => s.trim()).filter(Boolean) : [];
    const certificationsArray = certifications ? certifications.split('\n').map(s => s.trim()).filter(Boolean) : [];

    // Get sections in order
    const getSectionContent = (sectionId) => {
        switch(sectionId) {
            case 'summary':
                return summary ? (
                    <div className="mb-5">
                        <h2 style={{ color }} className="font-bold text-lg border-b-2 pb-2 mb-3">Professional Summary</h2>
                        <p className="text-gray-700 text-sm leading-relaxed">{escapeHtml(summary)}</p>
                    </div>
                ) : null;
            case 'education':
                return education.length > 0 ? (
                    <div className="mb-5">
                        <h2 style={{ color }} className="font-bold text-lg border-b-2 pb-2 mb-3">Education</h2>
                        {education.map((edu, idx) => (
                            <div key={idx} className="mb-3">
                                <div className="font-semibold text-gray-800">{escapeHtml(edu.degree)}</div>
                                <div className="text-gray-600 text-sm">{escapeHtml(edu.institution)}</div>
                                <div className="text-gray-500 text-xs">{escapeHtml(edu.year)}</div>
                                {edu.grade && <div className="text-gradpath-green text-sm font-medium mt-1">{escapeHtml(edu.grade)}</div>}
                            </div>
                        ))}
                    </div>
                ) : null;
            case 'experience':
                return experience.length > 0 ? (
                    <div className="mb-5">
                        <h2 style={{ color }} className="font-bold text-lg border-b-2 pb-2 mb-3">Work Experience</h2>
                        {experience.map((exp, idx) => (
                            <div key={idx} className="mb-4">
                                <div className="font-semibold text-gray-800">{escapeHtml(exp.title)} at {escapeHtml(exp.company)}</div>
                                <div className="text-gray-500 text-xs mb-1">{escapeHtml(exp.years)}</div>
                                <p className="text-gray-600 text-sm whitespace-pre-line">{escapeHtml(exp.responsibilities)}</p>
                            </div>
                        ))}
                    </div>
                ) : null;
            case 'skills':
                return skillsArray.length > 0 ? (
                    <div className="mb-5">
                        <h2 style={{ color }} className="font-bold text-lg border-b-2 pb-2 mb-3">Skills</h2>
                        <ul className="list-disc list-inside text-gray-700 text-sm space-y-0.5">
                            {skillsArray.map((skill, idx) => (
                                <li key={idx}>{escapeHtml(skill)}</li>
                            ))}
                        </ul>
                    </div>
                ) : null;
            case 'certifications':
                return certificationsArray.length > 0 ? (
                    <div className="mb-5">
                        <h2 style={{ color }} className="font-bold text-lg border-b-2 pb-2 mb-3">Certifications & Awards</h2>
                        <ul className="list-disc list-inside text-gray-700 text-sm space-y-0.5">
                            {certificationsArray.map((cert, idx) => (
                                <li key={idx}>{escapeHtml(cert)}</li>
                            ))}
                        </ul>
                    </div>
                ) : null;
            case 'languages':
                return languagesArray.length > 0 ? (
                    <div className="mb-5">
                        <h2 style={{ color }} className="font-bold text-lg border-b-2 pb-2 mb-3">Languages</h2>
                        <ul className="list-disc list-inside text-gray-700 text-sm space-y-0.5">
                            {languagesArray.map((lang, idx) => (
                                <li key={idx}>{escapeHtml(lang)}</li>
                            ))}
                        </ul>
                    </div>
                ) : null;
            default:
                return null;
        }
    };

    // Modern Template
    const generateModernTemplate = () => (
        <div style={{ fontFamily: font }} className="max-w-3xl mx-auto bg-white rounded-xl overflow-hidden shadow-xl">
            <div style={{ backgroundColor: color }} className="text-white p-6">
                <h1 className="text-2xl font-bold mb-1">{escapeHtml(personal.name)}</h1>
                {personal.title && <p className="text-base opacity-90 mb-3">{escapeHtml(personal.title)}</p>}
                <div className="flex flex-wrap gap-4 text-sm opacity-90">
                    {personal.email && <div><i className="fas fa-envelope mr-1"></i> {escapeHtml(personal.email)}</div>}
                    {personal.phone && <div><i className="fas fa-phone mr-1"></i> {escapeHtml(personal.phone)}</div>}
                    {personal.location && <div><i className="fas fa-map-marker-alt mr-1"></i> {escapeHtml(personal.location)}</div>}
                </div>
            </div>
            <div className="p-6">
                {sectionOrder.map(sectionId => (
                    <React.Fragment key={sectionId}>
                        {getSectionContent(sectionId)}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    // Classic Template
    const generateClassicTemplate = () => (
        <div style={{ fontFamily: font }} className="max-w-3xl mx-auto bg-white p-8 shadow-xl">
            <div className="text-center border-b-2 pb-4 mb-6" style={{ borderColor: color }}>
                <h1 className="text-2xl font-bold uppercase tracking-wide" style={{ color }}>{escapeHtml(personal.name)}</h1>
                {personal.title && <p className="text-gray-600 text-sm mt-1">{escapeHtml(personal.title)}</p>}
                <div className="flex justify-center gap-4 text-xs text-gray-500 mt-2 flex-wrap">
                    {personal.email && <span>{escapeHtml(personal.email)}</span>}
                    {personal.phone && <span>{escapeHtml(personal.phone)}</span>}
                    {personal.location && <span>{escapeHtml(personal.location)}</span>}
                </div>
            </div>
            <div className="space-y-4">
                {sectionOrder.map(sectionId => (
                    <React.Fragment key={sectionId}>
                        {getSectionContent(sectionId)}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    // Minimal Template
    const generateMinimalTemplate = () => (
        <div style={{ fontFamily: font }} className="max-w-3xl mx-auto bg-white p-8 shadow-xl">
            <div className="mb-6">
                <h1 className="text-2xl font-light text-gray-900">{escapeHtml(personal.name)}</h1>
                {personal.title && <p className="text-gray-600 text-sm mt-1">{escapeHtml(personal.title)}</p>}
                <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-2">
                    {personal.email && <span>{escapeHtml(personal.email)}</span>}
                    {personal.phone && <span>{escapeHtml(personal.phone)}</span>}
                    {personal.location && <span>{escapeHtml(personal.location)}</span>}
                </div>
            </div>
            <hr className="mb-6" style={{ borderColor: color }} />
            <div className="space-y-4">
                {sectionOrder.map(sectionId => (
                    <React.Fragment key={sectionId}>
                        {getSectionContent(sectionId)}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    // Professional Template
    const generateProfessionalTemplate = () => (
        <div style={{ fontFamily: font }} className="max-w-3xl mx-auto bg-white p-8 shadow-xl">
            <div className="flex items-start gap-6 mb-6 pb-6 border-b-2" style={{ borderColor: color }}>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900">{escapeHtml(personal.name)}</h1>
                    {personal.title && <p className="text-gray-600 text-sm">{escapeHtml(personal.title)}</p>}
                </div>
                <div className="text-right text-xs text-gray-500 space-y-1">
                    {personal.email && <div>{escapeHtml(personal.email)}</div>}
                    {personal.phone && <div>{escapeHtml(personal.phone)}</div>}
                    {personal.location && <div>{escapeHtml(personal.location)}</div>}
                </div>
            </div>
            <div className="grid grid-cols-3 gap-6">
                <div className="col-span-2 space-y-4">
                    {sectionOrder.filter(id => ['summary', 'experience', 'education'].includes(id)).map(sectionId => (
                        <React.Fragment key={sectionId}>
                            {getSectionContent(sectionId)}
                        </React.Fragment>
                    ))}
                </div>
                <div className="space-y-4">
                    {sectionOrder.filter(id => ['skills', 'certifications', 'languages'].includes(id)).map(sectionId => (
                        <React.Fragment key={sectionId}>
                            {getSectionContent(sectionId)}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );

    const templates = {
        modern: generateModernTemplate,
        classic: generateClassicTemplate,
        minimal: generateMinimalTemplate,
        professional: generateProfessionalTemplate
    };

    const renderTemplate = templates[template] || templates.modern;

    return (
        <div id="cv-preview-content">
            {renderTemplate()}
        </div>
    );
};

export default CVPreview;